import { Download, Maximize2 } from 'lucide-react'
import { ImageGallery } from '../ImageGallery'
import { GenerationProgress } from '../GenerationProgress'

interface GeneratedImage {
  id: string
  url: string
  prompt: string
  model: string
  created_at: string
}

interface ImageResultPanelProps {
  isGenerating: boolean
  progress: any
  currentBatchImages: GeneratedImage[]
  generatedImages: GeneratedImage[]
  concurrency: number
  hasEnoughQuota: boolean
  onDownload: (url: string, filename?: string) => void
  onBatchDownload: () => void
  onPreview: (url: string, prompt: string) => void
  onSetPrompt: (prompt: string) => void
}

export function ImageResultPanel({
  isGenerating, progress, currentBatchImages, generatedImages,
  concurrency, hasEnoughQuota, onDownload, onBatchDownload, onPreview, onSetPrompt,
}: ImageResultPanelProps) {
  return (
    <div className="space-y-6">
      {/* Generation Progress */}
      {isGenerating && progress && (
        <div className="bg-[#161b22] rounded-2xl p-6 border border-cyan-900/30">
          <GenerationProgress progress={progress} />
        </div>
      )}

      {/* Current Batch Generated Images */}
      {currentBatchImages.length > 0 && (
        <div className="bg-[#161b22] rounded-2xl p-6 border border-cyan-900/30">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">
              最新生成 {currentBatchImages.length > 1 && `(${currentBatchImages.length} 张)`}
            </h3>
            {currentBatchImages.length > 1 && (
              <button
                onClick={onBatchDownload}
                className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 rounded-lg text-sm font-medium transition-colors text-white"
              >
                <Download className="w-4 h-4" />
                <span>批量下载</span>
              </button>
            )}
          </div>

          {/* Single Image Layout */}
          {currentBatchImages.length === 1 && (
            <div className="flex justify-center">
              <div className="relative group">
                <img
                  src={currentBatchImages[0].url}
                  alt={currentBatchImages[0].prompt}
                  className="max-w-full h-auto rounded-xl shadow-lg max-h-80 object-contain"
                />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-xl flex items-center justify-center gap-3">
                  <button
                    onClick={() => onPreview(currentBatchImages[0].url, currentBatchImages[0].prompt)}
                    className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
                  >
                    <Maximize2 className="w-4 h-4" />
                    <span>预览</span>
                  </button>
                  <button
                    onClick={() => onDownload(currentBatchImages[0].url, `generated-${currentBatchImages[0].id}.png`)}
                    className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    <span>下载</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Two Images Layout */}
          {currentBatchImages.length === 2 && (
            <div className="grid grid-cols-2 gap-4">
              {currentBatchImages.map((image, index) => (
                <div key={image.id} className="relative group">
                  <img
                    src={image.url}
                    alt={`${image.prompt} - ${index + 1}`}
                    className="w-full h-auto rounded-xl shadow-lg object-contain"
                  />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-xl flex items-center justify-center gap-2">
                    <button
                      onClick={() => onPreview(image.url, image.prompt)}
                      className="bg-white/20 hover:bg-white/30 text-white p-2 rounded-lg transition-colors"
                      title="预览"
                    >
                      <Maximize2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => onDownload(image.url, `generated-${image.id}.png`)}
                      className="bg-white/20 hover:bg-white/30 text-white p-2 rounded-lg transition-colors"
                      title="下载"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Four Images Grid Layout */}
          {currentBatchImages.length >= 3 && (
            <div className="grid grid-cols-2 gap-3">
              {currentBatchImages.slice(0, 4).map((image, index) => (
                <div key={image.id} className="relative group aspect-square">
                  <img
                    src={image.url}
                    alt={`${image.prompt} - ${index + 1}`}
                    className="w-full h-full rounded-xl shadow-lg object-cover"
                  />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-xl flex items-center justify-center gap-2">
                    <button
                      onClick={() => onPreview(image.url, image.prompt)}
                      className="bg-white/20 hover:bg-white/30 text-white p-2 rounded-lg transition-colors"
                      title="预览"
                    >
                      <Maximize2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => onDownload(image.url, `generated-${image.id}.png`)}
                      className="bg-white/20 hover:bg-white/30 text-white p-2 rounded-lg transition-colors"
                      title="下载"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-4 text-center text-sm text-gray-400 space-y-1">
            <p><strong>提示词:</strong> {currentBatchImages[0].prompt}</p>
            <p><strong>模型:</strong> {currentBatchImages[0].model}</p>
            <p><strong>生成时间:</strong> {new Date(currentBatchImages[0].created_at).toLocaleString()}</p>
          </div>
        </div>
      )}

      {/* Image Gallery */}
      {generatedImages.length > 0 && (
        <ImageGallery
          images={generatedImages}
          onDownload={onDownload}
        />
      )}

      {/* Empty State */}
      {!isGenerating && currentBatchImages.length === 0 && generatedImages.length === 0 && (
        <div className="bg-[#161b22] rounded-2xl p-12 border border-cyan-900/30 flex flex-col items-center justify-center min-h-[500px]">
          <div className="mb-8">
            <svg className="w-24 h-24 text-cyan-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
              <circle cx="18" cy="6" r="2" fill="currentColor" />
              <circle cx="20" cy="10" r="1.5" fill="currentColor" />
            </svg>
          </div>

          <h2 className="text-3xl font-bold text-white mb-3">Ready to Create</h2>
          <p className="text-gray-400 text-center mb-8 max-w-md">
            Enter a prompt on the left to start generating amazing visuals.
          </p>

          <div className="flex flex-wrap gap-3 justify-center">
            <button
              onClick={() => onSetPrompt('A futuristic cyberpunk city with neon lights, flying cars, and towering skyscrapers at night')}
              className="px-5 py-2.5 bg-cyan-900/20 border-2 border-cyan-900/50 hover:border-cyan-500/50 hover:bg-cyan-900/30 rounded-full text-cyan-400 font-medium transition-all"
            >
              Cyberpunk City
            </button>
            <button
              onClick={() => onSetPrompt('A cute watercolor painting of a fluffy cat with big eyes, soft pastel colors')}
              className="px-5 py-2.5 bg-cyan-900/20 border-2 border-cyan-900/50 hover:border-cyan-500/50 hover:bg-cyan-900/30 rounded-full text-cyan-400 font-medium transition-all"
            >
              Watercolor Cat
            </button>
            <button
              onClick={() => onSetPrompt('A detailed space station orbiting Earth, with astronauts and advanced technology, sci-fi style')}
              className="px-5 py-2.5 bg-cyan-900/20 border-2 border-cyan-900/50 hover:border-cyan-500/50 hover:bg-cyan-900/30 rounded-full text-cyan-400 font-medium transition-all"
            >
              Space Station
            </button>
          </div>

          {!hasEnoughQuota && (
            <div className="mt-6 p-4 bg-red-900/20 border border-red-500/30 rounded-xl max-w-md">
              <p className="text-red-400 text-sm text-center">
                {concurrency > 1
                  ? `需要至少 ${concurrency} 个配额才能生成 ${concurrency} 张图片`
                  : '配额已用完，请联系管理员'}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
